import React, { useMemo } from 'react';
import Joyride, { Step, CallBackProps, ACTIONS, LIFECYCLE, Placement, EVENTS, TooltipRenderProps } from 'react-joyride';
import { Button } from '../Button/Button';
import { Group } from '../Group/Group';
import { IconButton } from '../IconButton/IconButton';
import { useGaudiLocalization } from '../Provider/Provider';
const { colors } = require('@economic/gaudi-tokens');

export type TourTexts = {
    /** Text for back action button */
    back: string;
    /**
     * Text for close button.
     * This button is displayed if tour is not continuous - replacing the `Next` button.
     * It will pause the flow of the tour and close the tooltip
     */
    close: string;
    /**
     * Aria-label and title for close icon button in each step.
     * This button skips and completes the flow entirely
     */
    skip: string;
    /**
     * Text for last action button.
     * This button is displayed when user is on the last step of the tour
     */
    last: string;
    /**
     * Text for next action button.
     * This button is displayed if tour has more than one steps and is continuous
     */
    next: string;
    /** Aria label and title for beacon that will open the tour step */
    open: string;
};

export type TourStepProps = {
    /** Content can be any valid react node, for e.g. a `div` */
    children: React.ReactNode;
    /** Define the position of the tour's popup relative to the element is presenting */
    position?: Placement;
    /** The css selector of the html element you want to include in a tour */
    selector: string;
    /**
     * Show beacon for step.
     * A beacon is a styled component which indicates the current element to be presented.
     * For more informations about how to create a beacon, read [Joyride](https://docs.react-joyride.com) docs
     */
    showBeacon?: boolean;
    /** Text displayed above the children/content of the popup  */
    title: string;
};

const Tooltip = ({
    continuous,
    index,
    isLastStep,
    step,
    backProps,
    primaryProps,
    skipProps,
    tooltipProps,
    size,
    locale,
}: TooltipRenderProps & { locale: TourTexts }) => {
    return (
        <div {...tooltipProps} className="yt-tour__step bg-white p-4 w-88 rounded relative">
            <IconButton
                {...skipProps}
                appearance="discrete"
                icon="close"
                title={locale.skip}
                aria-label={locale.skip}
                className="absolute top-0 mt-1 right-0 mr-1"
            />
            {step.title && <h5>{step.title}</h5>}
            {step.content}
            <Group className="mt-4 justify-end">
                {index > 0 && (
                    <Button {...backProps} appearance="discrete">
                        {locale.back}
                    </Button>
                )}
                <Button {...primaryProps} appearance="primary">
                    {continuous
                        ? isLastStep
                            ? `${locale.last} (${index + 1}/${size})`
                            : `${locale.next} (${index + 1}/${size})`
                        : locale.close}
                </Button>
            </Group>
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const TourStep = (_props: TourStepProps): null => null;

export type TourProps = {
    /** Controls if [Joyride](https://docs.react-joyride.com) is active */
    autoStart?: boolean;
    /** Children should be one or more `Tour.Step` components */
    children: any;
    /** Are there more than one steps and should you be able to navigate between them using `Next` and `Previous` buttons */
    continuous?: boolean;
    /** Disable closing of tour when click on `Escape` */
    disableCloseOnEsc?: boolean;
    /** Don't close the presenting popup of the tour step when clicking outside it */
    disableOverlayClose?: boolean;
    /** Allows user to interact with the presented components (mouse and touch events) when the popup is visible */
    disableScrolling?: boolean;
    /* When user clicks on the close icon button, which will close the tour and complete the flow */
    onClose?: (step: TourStepProps) => void;
    /* When user completes the entire flow */
    onComplete?: Function;
    /** When step is ready */
    onReady?: (step: TourStepProps) => void;
    /* Allow mouse and touch events through the spotlight */
    spotlightClicks?: boolean;
};

export const Tour = (props: TourProps) => {
    const {
        texts: { tour },
    } = useGaudiLocalization();

    const { autoStart: run, onComplete, onClose, onReady, spotlightClicks, ...rest } = props;

    const steps = useMemo(() => {
        return React.Children.map(props.children, child => {
            const step: Step = {
                disableBeacon: !child.props.showBeacon,
                target: child.props.selector,
                placement: child.props.position,
                title: child.props.title,
                content: child.props.children,
            };
            return step;
        });
    }, [props.children]);

    const getStep = React.useCallback(
        (selector: string | HTMLElement) => props.children.find((child: any) => child.props.selector === selector)?.props,
        [props.children]
    );

    function callback(state: CallBackProps) {
        if (state.action === ACTIONS.SKIP && state.lifecycle === LIFECYCLE.COMPLETE) {
            if (!!onClose) {
                onClose(getStep(state.step.target));
            }
        }

        if (state.type === EVENTS.TOUR_END) {
            if (!!onComplete) {
                onComplete();
            }
        }

        if (state.lifecycle === LIFECYCLE.READY) {
            if (!!onReady) {
                onReady(getStep(state.step.target));
            }
        }
    }

    return (
        <Joyride
            {...rest}
            run={run}
            steps={steps}
            showProgress
            floaterProps={{
                disableAnimation: true,
            }}
            tooltipComponent={tooltipProps => <Tooltip {...tooltipProps} locale={tour} />}
            locale={tour}
            spotlightPadding={0}
            spotlightClicks={spotlightClicks}
            callback={callback}
            styles={{
                /** style beacon */
                options: {
                    primaryColor: colors.blue.light,
                },
            }}
        />
    );
};

Tour.Step = TourStep;
